import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/Icon';
import { Avatars, Chip, Ring } from '../../components/bits';
import { useStore } from '../../state/store';
import { MONTH_TOTAL, QUEUE } from '../../data/mock';
import { countPendingInvoices, getRecentCallSummary } from '../../data/api';
import { dayVisits, progressOf, projectedEnd, todayCounts } from '../../lib/derive';
import { fmtHour } from '../../lib/time';
import type { Channel, QueueItem } from '../../data/types';

/** Canales con integración lista hoy. Correo, redes y web → “Próximamente”. */
const LIVE_CHANNELS: Channel[] = ['call', 'missed', 'sms'];
const CH: Record<Channel, { color: string; bg: string; icon: string; label: string }> = {
  call: { color: '#16A34A', bg: '#DCFCE7', icon: 'phone', label: 'Llamadas' },
  missed: { color: '#E5484D', bg: '#FEE2E2', icon: 'missed', label: 'Perdidas' },
  sms: { color: '#2563EB', bg: '#DBEAFE', icon: 'sms', label: 'SMS' },
  mail: { color: '#7C3AED', bg: '#EDE9FE', icon: 'mail', label: 'Correo' },
  social: { color: '#DB2777', bg: '#FCE7F3', icon: 'social', label: 'Redes' },
  web: { color: '#0F8C7B', bg: '#E2F4F1', icon: 'web', label: 'Web' },
};

type WidgetId = 'ruta' | 'hoy' | 'asignar' | 'mes' | 'cobros' | 'flow' | 'estimados' | 'nomina';
const DEFAULT_ORDER: WidgetId[] = ['ruta', 'hoy', 'asignar', 'mes', 'cobros', 'flow', 'estimados', 'nomina'];

function loadLayout(): { order: WidgetId[]; hidden: WidgetId[] } {
  try {
    const raw = localStorage.getItem('fn.home.v2');
    if (raw) return JSON.parse(raw);
  } catch { /* sin almacenamiento */ }
  return { order: DEFAULT_ORDER, hidden: ['cobros', 'flow', 'estimados', 'nomina'] };
}

export function InicioPage() {
  const s = useStore();
  const [layout, setLayout] = useState(loadLayout);
  const [editing, setEditing] = useState(false);
  const [filter, setFilter] = useState<Channel | 'all'>('all');
  const [dialer, setDialer] = useState(false);

  const persist = (l: typeof layout) => { setLayout(l); try { localStorage.setItem('fn.home.v2', JSON.stringify(l)); } catch { /* */ } };
  const move = (id: WidgetId, d: -1 | 1) => {
    const o = [...layout.order]; const i = o.indexOf(id); const j = i + d;
    if (j < 0 || j >= o.length) return;
    [o[i], o[j]] = [o[j], o[i]]; persist({ ...layout, order: o });
  };
  const toggle = (id: WidgetId) => persist({ ...layout, hidden: layout.hidden.includes(id) ? layout.hidden.filter(x => x !== id) : [...layout.hidden, id] });

  const onQueue = (q: QueueItem) => {
    if (!LIVE_CHANNELS.includes(q.channel)) {
      s.showSoon({ title: `${CH[q.channel].label} en Acciones en cola`, body: 'La integración de correo, redes sociales y formularios web llega después. La interfaz ya está lista para recibirlos.' });
      return;
    }
    if (q.channel === 'call') { s.setCaller({ name: q.kind === 'Desconocido' ? '' : q.who, phone: q.text.split('· ').pop() }); s.go('espacio'); return; }
    if (q.action === 'Reagendar') { s.setAgendaMode('calendario'); s.go('agenda'); return; }
    s.notify('Se abre Flow Mobile para devolver la llamada (integración existente).');
  };

  // TODO(API): unify the existing Flow Mobile call/SMS feeds with JobEventChat
  // before replacing the synthetic queue. This preview never answers or sends.
  const items = QUEUE.filter(q => filter === 'all' || q.channel === filter);
  const visible = layout.order.filter(id => editing || !layout.hidden.includes(id));

  return (
    <div className="content">
      <div className="row wrap" style={{ alignItems: 'flex-start', gap: 18 }}>
        <section className="w queue" aria-label="Acciones en cola">
          <div className="wh" style={{ padding: '0 4px' }}>
            <h2 style={{ fontSize: 17 }}>Acciones en cola</h2>
            <div className="row" style={{ gap: 6 }}>
              <span className="badge" style={{ fontSize: 12, padding: '2px 8px' }}>{QUEUE.length}</span>
              <button type="button" aria-label="Abrir teléfono para llamar" onClick={() => setDialer(true)}
                style={{ width: 40, height: 40, borderRadius: 12, background: '#16A34A', border: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 4px #DCFCE7', cursor: 'pointer' }}>
                <Icon name="phone" color="#fff" size={19} />
              </button>
            </div>
          </div>
          <div className="row wrap" style={{ gap: 6, padding: '0 4px' }} role="group" aria-label="Filtrar por canal">
            <button type="button" className={`pill${filter === 'all' ? ' on' : ''}`} style={{ minHeight: 32 }} onClick={() => setFilter('all')}>Todo <b>{QUEUE.length}</b></button>
            {(Object.keys(CH) as Channel[]).filter(c => c !== 'missed').map(c => {
              const n = QUEUE.filter(q => q.channel === c).length;
              const soon = !LIVE_CHANNELS.includes(c);
              return (
                <button key={c} type="button" className={`pill${filter === c ? ' on' : ''}`} style={{ minHeight: 32, padding: '0 9px' }}
                  aria-label={`${CH[c].label}${soon ? ', próximamente' : ''}`}
                  onClick={() => (soon ? s.showSoon({ title: CH[c].label, body: 'Conectar este canal llega después. Cuando esté listo, sus mensajes aparecen aquí con su acción sugerida.' }) : setFilter(c))}>
                  <Icon name={CH[c].icon} color={CH[c].color} size={14} />{soon ? <span className="soon">pronto</span> : <b>{n}</b>}
                </button>
              );
            })}
          </div>
          <div>
            {items.map(q => {
              const c = CH[q.channel];
              const soon = !LIVE_CHANNELS.includes(q.channel);
              const kind = { Cliente: ['#E2F4F1', '#0B6B5E'], Prospecto: ['#FFF1D6', '#8A4B00'], Desconocido: ['#F0EFEB', '#5E5F63'] }[q.kind];
              return (
                <button key={q.id} type="button" className={`q${q.ringing ? ' hot' : ''}`} onClick={() => onQueue(q)}>
                  <span className="chn" style={{ background: c.bg, boxShadow: q.ringing ? `0 0 0 4px ${c.bg}` : undefined, opacity: soon ? 0.6 : 1 }}><Icon name={c.icon} color={c.color} size={19} /></span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span className="row" style={{ gap: 6 }}><b>{q.who}</b><span className="tag" style={{ background: kind[0], color: kind[1] }}>{q.kind}</span>
                      {soon && <span className="soon">próximamente</span>}<span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>{q.when}</span></span>
                    <span className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.text}</span>
                    <span><span className="chip" style={{ background: q.ringing ? '#16A34A' : soon ? '#8A8B8E' : '#1F1F21', color: '#fff' }}>{q.action}</span></span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <div style={{ flex: '999 1 640px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="row wrap" style={{ justifyContent: 'space-between' }}>
            <h1 className="h1">Jueves 29 de octubre</h1>
            <button type="button" className="btn" style={{ minHeight: 38 }} aria-pressed={editing} onClick={() => setEditing(e => !e)}>
              <Icon name="grid" size={15} />{editing ? 'Listo' : 'Personalizar'}
            </button>
          </div>
          <div className="home-grid">
            {visible.map((id, i) => (
              <Widget key={id} id={id} editing={editing} hidden={layout.hidden.includes(id)}
                onUp={i > 0 ? () => move(id, -1) : undefined} onDown={i < visible.length - 1 ? () => move(id, 1) : undefined} onToggle={() => toggle(id)} />
            ))}
          </div>
        </div>
      </div>
      {dialer && <Dialer onClose={() => setDialer(false)} />}
    </div>
  );
}

function Widget({ id, editing, hidden, onUp, onDown, onToggle }: { id: WidgetId; editing: boolean; hidden: boolean; onUp?(): void; onDown?(): void; onToggle(): void }) {
  const s = useStore();
  const navigate = useNavigate();
  const [pendingInvoices, setPendingInvoices] = useState<number | null | undefined>();
  const [calls, setCalls] = useState<{ recent: number; answered: number; missed: number } | null | undefined>();
  useEffect(() => {
    let active = true;
    if (id === 'cobros') countPendingInvoices().then(n => { if (active) setPendingInvoices(n); }).catch(() => { if (active) setPendingInvoices(null); });
    if (id === 'flow') getRecentCallSummary().then(n => { if (active) setCalls(n); }).catch(() => { if (active) setCalls(null); });
    return () => { active = false; };
  }, [id]);
  const snap = s.snapshot;
  const counts = todayCounts(snap);
  const W = (title: string, body: ReactNode, opts: { span?: number; link?: [string, () => void]; soon?: boolean } = {}) => (
    <section className="w" style={{ gridColumn: `span ${opts.span ?? 1}`, opacity: hidden ? 0.45 : 1 }} aria-label={title}>
      <div className="wh">
        <div className="row" style={{ gap: 8 }}><span className="grip" aria-hidden="true">⋮⋮</span><h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>{opts.soon && <span className="soon">próximamente</span>}</div>
        {editing ? (
          <div className="row" style={{ gap: 4 }}>
            <button type="button" className="btn sm" aria-label="Subir" disabled={!onUp} onClick={onUp}><Icon name="up" size={14} /></button>
            <button type="button" className="btn sm" aria-label="Bajar" disabled={!onDown} onClick={onDown}><Icon name="down" size={14} /></button>
            <button type="button" className="btn sm" onClick={onToggle}>{hidden ? 'Mostrar' : 'Ocultar'}</button>
          </div>
        ) : opts.link && <button type="button" className="muted" style={{ background: 'none', border: 0, fontSize: 12, fontWeight: 700, cursor: 'pointer' }} onClick={opts.link[1]}>{opts.link[0]} ›</button>}
      </div>
      {body}
    </section>
  );
  const toAgenda = (m: 'calendario' | 'envivo' | 'estado') => () => { s.setAgendaMode(m); s.go('agenda'); };

  switch (id) {
    case 'ruta':
      return W('Día en ruta', (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {snap.teams.map(t => {
            const vs = dayVisits(snap, snap.today, t.id);
            const stops = [{ label: `Salida ${fmtHour(Math.min(...vs.map(v => v.start), t.workStart)).replace(':00', '')}`, state: 'done' as const, pct: null as number | null },
              ...vs.map(v => ({ label: shortName(snap.clients.find(c => c.uid === v.clientUid)?.name ?? ''), state: v.status === 'completed' ? 'done' as const : v.status === 'inProgress' ? 'now' as const : 'next' as const, pct: progressOf(v, snap.now).real })),
              { label: 'Fin', state: 'next' as const, pct: null }];
            const cur = vs.find(v => v.status === 'inProgress');
            const lastDone = stops.reduce((acc, st, i) => (st.state !== 'next' ? i : acc), 0);
            const end = Math.max(...vs.map(v => v.end), cur ? projectedEnd(cur, snap.now) ?? 0 : 0);
            return (
              <div key={t.id} className="row" style={{ gap: 12 }}>
                <div style={{ flex: '0 0 70px' }}><b style={{ fontSize: 13, color: t.color }}>{t.name}</b><br /><Avatars team={t} color={t.color} size={20} /></div>
                <div style={{ position: 'relative', flex: 1, height: 58, margin: '0 16px' }}>
                  <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 4, marginTop: -2, borderRadius: 9, background: `repeating-linear-gradient(90deg, ${t.color}55 0 6px, transparent 6px 11px)` }} />
                  <div style={{ position: 'absolute', left: 0, width: `${(lastDone / (stops.length - 1)) * 100}%`, top: '50%', height: 4, marginTop: -2, borderRadius: 9, background: t.color }} />
                  {stops.map((st, i) => (
                    <div key={i} style={{ position: 'absolute', left: `${(i / (stops.length - 1)) * 100}%`, top: '50%', transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ height: 12 }} />
                      {st.state === 'now'
                        ? <Ring pct={st.pct ?? 0} color={t.color} size={30} thickness={4} fontSize={9} />
                        : <span style={{ width: 14, height: 14, borderRadius: '50%', background: st.state === 'done' ? t.color : '#fff', border: st.state === 'done' ? undefined : `2.5px solid ${t.color}` }} />}
                      <span className="route-lbl" style={{ fontSize: 10.5, fontWeight: 700, color: '#5E5F63', whiteSpace: 'nowrap' }}>{st.label}</span>
                    </div>
                  ))}
                </div>
                <span className="muted" style={{ flex: '0 0 78px', fontSize: 11, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' }}>fin ~{fmtHour(end)}</span>
              </div>
            );
          })}
        </div>
      ), { span: 2, link: ['Abrir mapa', toAgenda('envivo')] });
    case 'hoy':
      return W('Hoy', (
        <div className="row" style={{ gap: 14 }}>
          <div className="ring" style={{ width: 70, height: 70, background: segments(counts) }}><span style={{ inset: 9, fontSize: 22 }}>{counts.total}</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, fontWeight: 700 }}>
            <span>● {counts.done} hechos</span><span style={{ color: '#2563EB' }}>● {counts.running} en curso</span>
            <span style={{ color: '#B7791F' }}>● {counts.pending} por llegar</span><span style={{ color: '#E5484D' }}>● {counts.risk} en riesgo</span>
          </div>
        </div>
      ), { link: ['Calendario', toAgenda('calendario')] });
    case 'asignar':
      return W('Por asignar', (
        <div className="row"><div className="ring" style={{ width: 56, height: 56, background: 'var(--fn-gradient)' }}><span style={{ inset: 7, fontSize: 20 }}>2</span></div>
          <span className="muted" style={{ fontSize: 13 }}>sin equipo</span></div>
      ), { link: ['Asignar', toAgenda('calendario')] });
    case 'mes':
      return W('Octubre', (
        <div className="row" style={{ gap: 14 }}><Ring pct={((MONTH_TOTAL.done + MONTH_TOTAL.scheduled) / MONTH_TOTAL.projected) * 100} color="#1F1F21" size={70} thickness={9} fontSize={14} />
          <div><span className="big">{MONTH_TOTAL.done + MONTH_TOTAL.scheduled}</span><div className="muted" style={{ fontSize: 12 }}>de ~{MONTH_TOTAL.projected} trabajos</div></div></div>
      ), { span: 2, link: ['Estado', toAgenda('estado')] });
    case 'cobros': {
      return W('Cobros', (
        <>
          <div><span className="big">{pendingInvoices === undefined ? '…' : pendingInvoices === null ? '—' : pendingInvoices}</span><div className="muted" style={{ fontSize: 12 }}>facturas pendientes {pendingInvoices === null ? '· dato no disponible' : '· dato real'}</div></div>
          <div className="row wrap" style={{ gap: 6 }}>
            <Chip bg="#F0EFEB" fg="#3A3A3D">Importe y vencimiento: no disponibles aquí</Chip>
          </div>
        </>
      ), { link: ['Cobros', () => navigate('/business/payments')] });
    }
    case 'flow':
      return W('Flow Mobile reciente', (
        <>
          <div className="row" style={{ gap: 14, fontSize: 12, fontWeight: 700 }}>
            <span><b style={{ fontSize: 18 }}>{calls === undefined ? '…' : calls?.recent ?? '—'}</b> últimas llamadas</span>
            <span style={{ color: '#16A34A' }}><b style={{ fontSize: 18 }}>{calls === undefined ? '…' : calls?.answered ?? '—'}</b> entrantes contestadas</span>
            <span style={{ color: '#E5484D' }}><b style={{ fontSize: 18 }}>{calls === undefined ? '…' : calls?.missed ?? '—'}</b> sin respuesta</span>
          </div>
        </>
      ), { link: ['Abrir', () => navigate('/business/contact-center')] });
    case 'estimados':
      return W('Estimados', <span className="muted" style={{ fontSize: 13 }}>Resumen de estimados enviados, por vencer y aceptados.</span>, { soon: true });
    case 'nomina':
      return W('Nómina', <span className="muted" style={{ fontSize: 13 }}>Horas de la semana por equipo y próximo pago.</span>, { soon: true });
  }
}

function Dialer({ onClose }: { onClose(): void }) {
  const s = useStore();
  const sugg = [
    { who: 'Oficina Nova', why: 'Seguimiento a su cotización · prospecto', icon: 'mail', c: '#7C3AED', bg: '#EDE9FE' },
    { who: '(512) 555-0190', why: 'Llamada perdida 9:58 · devolver', icon: 'missed', c: '#E5484D', bg: '#FEE2E2' },
    { who: 'Marta Linares', why: 'Confirmar cambio al sábado', icon: 'sms', c: '#2563EB', bg: '#DBEAFE' },
  ];
  const [pick, setPick] = useState(0);
  return (
    <div className="backdrop" role="dialog" aria-modal="true" aria-label="Flow Mobile · nueva llamada" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760, padding: 0, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div className="row" style={{ padding: '14px 18px', background: '#1F1F21', color: '#fff' }}>
          <span className="chn" style={{ background: '#16A34A' }}><Icon name="phone" color="#fff" size={18} /></span>
          <b style={{ fontSize: 16 }}>Flow Mobile · nueva llamada</b>
          <span className="chip" style={{ marginLeft: 'auto', background: '#3A3A3D', color: '#fff' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#E5484D' }} />Se graba</span>
          <button type="button" aria-label="Cerrar" onClick={onClose} style={{ background: 'none', border: 0, color: '#fff', cursor: 'pointer' }}><Icon name="close" size={16} /></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)' }}>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, borderRight: '1px solid #F0EFEB' }}>
            <label className="search"><Icon name="search" color="#8A8B8E" size={16} /><span className="sr-only">Buscar contacto</span><input placeholder="Nombre o número…" /></label>
            <b style={{ fontSize: 13 }}>Sugeridas por tus pendientes</b>
            {sugg.map((x, i) => (
              <button key={x.who} type="button" onClick={() => setPick(i)} className="row" style={{ padding: 10, borderRadius: 12, border: 0, cursor: 'pointer', textAlign: 'left', background: pick === i ? '#FFF7E3' : '#FAFAF8', boxShadow: pick === i ? 'inset 0 0 0 2px #1F1F21' : undefined }}>
                <span className="chn" style={{ width: 34, height: 34, background: x.bg }}><Icon name={x.icon} color={x.c} size={17} /></span>
                <span style={{ flex: 1 }}><b style={{ fontSize: 13 }}>{x.who}</b><br /><span className="muted" style={{ fontSize: 12 }}>{x.why}</span></span>
              </button>
            ))}
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <b style={{ fontSize: 18 }}>{sugg[pick].who}</b>
            <div className="field"><span>Llamar desde</span><div className="inp row" style={{ display: 'flex' }}><Icon name="phone" color="#16A34A" size={15} />Brisa Clean Co. · (512) 555-0100</div></div>
            <div style={{ borderRadius: 14, background: '#FAFAF8', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <b>Guía para esta llamada</b>
              <span>1. Saluda como <b>Brisa Clean Co.</b> y avisa que la llamada se graba.</span>
              <span>2. {sugg[pick].why}.</span>
              <span>3. Si necesita agendar, usa <b>¿Hay espacio?</b> sin colgar.</span>
              <span>4. Si no contesta, deja mensaje; se crea recordatorio.</span>
            </div>
            <button type="button" className="btn" style={{ background: '#16A34A', color: '#fff', border: 0, minHeight: 52, fontSize: 16 }}
              onClick={() => { onClose(); s.notify('Flow Mobile marcaría ahora (integración existente). Llamada grabada.'); }}>
              <Icon name="phone" color="#fff" size={18} />Llamar a {sugg[pick].who}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function shortName(n: string): string {
  return n.replace(/^(Casa|Depto\.|Oficinas|Oficina|Consultorio)\s+/, '').replace(/\s*&.*$/, '').split(' ')[0];
}

function segments(c: { total: number; done: number; running: number; pending: number; risk: number }) {
  const t = Math.max(c.total, 1);
  const a = (c.done / t) * 360, b = a + (c.running / t) * 360, d = b + (c.pending / t) * 360;
  return `conic-gradient(#1F1F21 0 ${a}deg, #2563EB 0 ${b}deg, #FAAF3B 0 ${d}deg, #E5484D 0 360deg)`;
}
